/**
 * A colour, chosen the way every word processor lets you choose one.
 *
 * SIX SWATCHES WAS A PLACEHOLDER. A controlled document's tables are colour-coded by hand -- a
 * caution row, a revision block, a shaded column -- and a picker that offers six colours is a
 * picker that sends people to look for another tool.
 *
 * THE PALETTE IS GOOGLE'S OWN: ten greys, then ten hues in six steps each. Not because it is
 * pretty, but because it is the palette the documents being pasted in were written with -- choosing
 * a different eighty would mean a table could never be made to match the one beside it.
 *
 * AND A CUSTOM FIELD, because eighty is not all of them. It takes a hex value, which is what the
 * document stores and what the rendition reads -- there is no colour here that the PDF cannot draw.
 */
import React from "react";
import { Box, Button, Popover, TextField, Tooltip, Typography } from "@mui/material";

import Symbol from "./Symbol";

/** Ten greys, then ten hues: base, three tints, three shades. 80 in all. */
const GREYS = ["#000000", "#434343", "#666666", "#999999", "#B7B7B7",
               "#CCCCCC", "#D9D9D9", "#EFEFEF", "#F3F3F3", "#FFFFFF"];

const HUES = [
    ["#980000", "#FF0000", "#FF9900", "#FFFF00", "#00FF00", "#00FFFF", "#4A86E8", "#0000FF", "#9900FF", "#FF00FF"],
    ["#E6B8AF", "#F4CCCC", "#FCE5CD", "#FFF2CC", "#D9EAD3", "#D0E0E3", "#C9DAF8", "#CFE2F3", "#D9D2E9", "#EAD1DC"],
    ["#DD7E6B", "#EA9999", "#F9CB9C", "#FFE599", "#B6D7A8", "#A2C4C9", "#A4C2F4", "#9FC5E8", "#B4A7D6", "#D5A6BD"],
    ["#CC4125", "#E06666", "#F6B26B", "#FFD966", "#93C47D", "#76A5AF", "#6D9EEB", "#6FA8DC", "#8E7CC3", "#C27BA0"],
    ["#A61C00", "#CC0000", "#E69138", "#F1C232", "#6AA84F", "#45818E", "#3C78D8", "#3D85C6", "#674EA7", "#A64D79"],
    ["#85200C", "#990000", "#B45F06", "#BF9000", "#38761D", "#134F5C", "#1155CC", "#0B5394", "#351C75", "#741B47"],
    ["#5B0F00", "#660000", "#783F04", "#7F6000", "#274E13", "#0C343D", "#1C4587", "#073763", "#20124D", "#4C1130"],
];

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const normalise = (value: string) => {
    const hex = value.trim().replace(/^#/, "");
    if (!HEX.test(hex)) return null;
    const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
    return `#${full.toUpperCase()}`;
};

const same = (a: string | null, b: string | null) =>
    (a || "").toUpperCase() === (b || "").toUpperCase();

function Swatch({ colour, selected, onPick }: {
    colour: string | null; selected: boolean; onPick: () => void;
}) {
    return (
        <Tooltip title={colour ?? "None"} placement="top" enterDelay={400}>
            <Box role="button" tabIndex={0} aria-label={colour ?? "None"} onClick={onPick}
                 onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(); } }}
                 sx={{
                     width: 18, height: 18, borderRadius: "3px", cursor: "pointer",
                     backgroundColor: colour ?? "#FFFFFF",
                     // A WHITE SWATCH NEEDS AN EDGE or it is a hole in the grid; so does the empty
                     // one, which has to read as "no colour" rather than "white".
                     border: selected ? "2px solid #1498D8"
                           : colour === null || same(colour, "#FFFFFF") ? "1px solid #BDC1C6"
                           : "1px solid rgba(0,0,0,.12)",
                     display: "flex", alignItems: "center", justifyContent: "center",
                     "&:hover": { outline: "1px solid #1498D8" },
                 }}>
                {colour === null && <Symbol name="format_color_reset" size={12} colour="#9AA0A6"/>}
            </Box>
        </Tooltip>
    );
}

export default function ColourPicker({ value, onChange, allowNone, label, disabled }: {
    value: string | null;
    onChange: (next: string | null) => void;
    /** Whether "no colour" is a legal answer -- true for a background, false for a border. */
    allowNone?: boolean;
    label: string;
    disabled?: boolean;
}) {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    const [custom, setCustom] = React.useState("");

    const pick = (colour: string | null) => { onChange(colour); setAnchor(null); };

    return (
        <>
            <Box role="button" tabIndex={disabled ? -1 : 0} aria-label={label}
                 onClick={(e) => !disabled && setAnchor(e.currentTarget)}
                 onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " ")) {
                     e.preventDefault(); setAnchor(e.currentTarget as HTMLElement); } }}
                 sx={{
                     display: "inline-flex", alignItems: "center", gap: 0.5, px: 0.75, py: 0.5,
                     border: "1px solid #D8DDE4", borderRadius: 1,
                     cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1,
                 }}>
                <Box sx={{
                    width: 18, height: 18, borderRadius: "3px",
                    backgroundColor: value ?? "#FFFFFF",
                    border: value && !same(value, "#FFFFFF") ? "1px solid rgba(0,0,0,.12)"
                                                             : "1px solid #BDC1C6",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    {!value && <Symbol name="format_color_reset" size={12} colour="#9AA0A6"/>}
                </Box>
                <Symbol name="arrow_drop_down" size={16} colour="#525968"/>
            </Box>

            <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
                     anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                     slotProps={{ paper: { sx: { p: 1.5, borderRadius: 2 } } }}>
                {allowNone && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <Swatch colour={null} selected={!value} onPick={() => pick(null)}/>
                        <Typography sx={{ fontSize: 12, color: "#525968" }}>None</Typography>
                    </Box>
                )}

                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(10, 18px)", gap: 0.5 }}>
                    {GREYS.map((colour) => (
                        <Swatch key={colour} colour={colour} selected={same(value, colour)}
                                onPick={() => pick(colour)}/>
                    ))}
                </Box>
                <Box sx={{ height: 8 }}/>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(10, 18px)", gap: 0.5 }}>
                    {HUES.flat().map((colour) => (
                        <Swatch key={colour} colour={colour} selected={same(value, colour)}
                                onPick={() => pick(colour)}/>
                    ))}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                    <TextField size="small" placeholder="#RRGGBB" value={custom}
                               onChange={(e) => setCustom(e.target.value)}
                               onKeyDown={(e) => {
                                   if (e.key !== "Enter") return;
                                   const hex = normalise(custom);
                                   if (hex) { pick(hex); setCustom(""); }
                               }}
                               slotProps={{ htmlInput: { style: { fontSize: 12, padding: "6px 8px", width: 92 } } }}/>
                    {/* DISABLED UNTIL IT IS A COLOUR. A hex field that accepts "blue" and then
                        silently does nothing is worse than one that will not take it. */}
                    <Button size="small" disabled={!normalise(custom)}
                            onClick={() => { const hex = normalise(custom); if (hex) { pick(hex); setCustom(""); } }}
                            sx={{ textTransform: "none", fontSize: 12 }}>
                        Custom
                    </Button>
                </Box>
            </Popover>
        </>
    );
}
